import React, { useState, useEffect, Suspense, useMemo } from 'react';
import { useAppContext } from '../context/AppContext';
import PropTypes from 'prop-types';
import SelectedPlantInfoMobile from '../components/SelectedPlantInfo.mobile';
import gardenBedLayouts from '../data/gardenBedLayouts.json';
import { Box, Text, Group, Button, Stack } from '@mantine/core';
import { colors, fontSize } from '../constants';

export const componentMap = {
	1: React.lazy(() => import('../components/garden-beds/OneTwelveEnglish')),
	2: React.lazy(() => import('../components/garden-beds/OneTwelveFormal')),
	3: React.lazy(() => import('../components/garden-beds/OneTwelveTropical')),
	4: React.lazy(() => import('../components/garden-beds/OneTwelveWaterWise')),
	5: React.lazy(() => import('../components/garden-beds/OneFifteenEnglish')),
	6: React.lazy(() => import('../components/garden-beds/OneFifteenFormal')),
	7: React.lazy(() => import('../components/garden-beds/OneFifteenTropical')),
	8: React.lazy(() => import('../components/garden-beds/OneFifteenWaterWise')),
	9: React.lazy(() => import('../components/garden-beds/OneEighteenEnglish')),
	10: React.lazy(() => import('../components/garden-beds/OneEighteenFormal')),
	11: React.lazy(() => import('../components/garden-beds/OneEighteenTropical')),
	12: React.lazy(() => import('../components/garden-beds/OneEighteenWaterWise')),
	13: React.lazy(() => import('../components/garden-beds/TwoTwelveEnglish')),
	14: React.lazy(() => import('../components/garden-beds/TwoTwelveFormal')),
	15: React.lazy(() => import('../components/garden-beds/TwoTwelveTropical')),
	16: React.lazy(() => import('../components/garden-beds/TwoTwelveWaterWise')),
	17: React.lazy(() => import('../components/garden-beds/TwoFifteenEnglish')),
	18: React.lazy(() => import('../components/garden-beds/TwoFifteenFormal')),
	19: React.lazy(() => import('../components/garden-beds/TwoFifteenTropical')),
	20: React.lazy(() => import('../components/garden-beds/TwoFifteenWaterWise')),
	21: React.lazy(() => import('../components/garden-beds/TwoEighteenEnglish')),
	22: React.lazy(() => import('../components/garden-beds/TwoEighteenFormal')),
	23: React.lazy(() => import('../components/garden-beds/TwoEighteenTropical')),
	24: React.lazy(() => import('../components/garden-beds/TwoEighteenWaterWise')),
	25: React.lazy(() => import('../components/garden-beds/ThreeTwelveEnglish')),
	26: React.lazy(() => import('../components/garden-beds/ThreeTwelveFormal')),
	27: React.lazy(() => import('../components/garden-beds/ThreeTwelveTropical')),
	28: React.lazy(() => import('../components/garden-beds/ThreeTwelveWaterWise')),
	29: React.lazy(() => import('../components/garden-beds/ThreeFifteenEnglish')),
	30: React.lazy(() => import('../components/garden-beds/ThreeFifteenFormal')),
	31: React.lazy(() => import('../components/garden-beds/ThreeFifteenTropical')),
	32: React.lazy(() => import('../components/garden-beds/ThreeFifteenWaterWise')),
	33: React.lazy(() => import('../components/garden-beds/ThreeEighteenEnglish')),
	34: React.lazy(() => import('../components/garden-beds/ThreeEighteenFormal')),
	35: React.lazy(() => import('../components/garden-beds/ThreeEighteenTropical')),
	36: React.lazy(() => import('../components/garden-beds/ThreeEighteenWaterWise')),
	37: React.lazy(() => import('../components/garden-beds/FourTwelveEnglish')),
	38: React.lazy(() => import('../components/garden-beds/FourTwelveFormal')),
	39: React.lazy(() => import('../components/garden-beds/FourTwelveTropical')),
	40: React.lazy(() => import('../components/garden-beds/FourTwelveWaterWise')),
	41: React.lazy(() => import('../components/garden-beds/FourFifteenEnglish')),
	42: React.lazy(() => import('../components/garden-beds/FourFifteenFormal')),
	43: React.lazy(() => import('../components/garden-beds/FourFifteenTropical')),
	44: React.lazy(() => import('../components/garden-beds/FourFifteenWaterWise')),
	45: React.lazy(() => import('../components/garden-beds/FourEighteenEnglish')),
	46: React.lazy(() => import('../components/garden-beds/FourEighteenFormal')),
	47: React.lazy(() => import('../components/garden-beds/FourEighteenTropical')),
	48: React.lazy(() => import('../components/garden-beds/FourEighteenWaterWise')),
	49: React.lazy(() => import('../components/garden-beds/FiveTwelveEnglish')),
	50: React.lazy(() => import('../components/garden-beds/FiveTwelveFormal')),
	51: React.lazy(() => import('../components/garden-beds/FiveTwelveTropical')),
	52: React.lazy(() => import('../components/garden-beds/FiveTwelveWaterWise')),
	53: React.lazy(() => import('../components/garden-beds/FiveFifteenEnglish')),
	54: React.lazy(() => import('../components/garden-beds/FiveFifteenFormal')),
	55: React.lazy(() => import('../components/garden-beds/FiveFifteenTropical')),
	56: React.lazy(() => import('../components/garden-beds/FiveFifteenWaterWise')),
	57: React.lazy(() => import('../components/garden-beds/FiveEighteenEnglish')),
	58: React.lazy(() => import('../components/garden-beds/FiveEighteenFormal')),
	59: React.lazy(() => import('../components/garden-beds/FiveEighteenTropical')),
	60: React.lazy(() => import('../components/garden-beds/FiveEighteenWaterWise')),
};

const ChoosePlantSideWindowMobile = ({ onCategoryChange, selectedPlantData }) => {
	const [currentPlantCategory, setCurrentPlantCategory] = useState(null);
	const [currentCategoryIndex, setCurrentCategoryIndex] = useState(0);
	const { state } = useAppContext();
	const { bedConfig } = state.currentDesign || {};
	
	const matchingGardenBed = bedConfig ? gardenBedLayouts.find((layout) => layout.id === bedConfig) : null;
	const plantCategoriesList = useMemo(() => {
		return matchingGardenBed ? matchingGardenBed.plants.map((plant) => plant.category) : [];
	}, [matchingGardenBed]);

	// Find and create category list associated with the filtered garden bed
	useEffect(() => {
		if (plantCategoriesList.length > 0) {
			setCurrentPlantCategory(plantCategoriesList[currentCategoryIndex]);
			onCategoryChange(plantCategoriesList[currentCategoryIndex]);
		}
	}, [plantCategoriesList, currentCategoryIndex, onCategoryChange]);

	// Category navigation buttons
	const goToNextCategory = () => {
		setCurrentCategoryIndex((prevIndex) =>
			prevIndex < plantCategoriesList.length - 1 ? prevIndex + 1 : prevIndex
		);
	};
	const goToPreviousCategory = () => {
		setCurrentCategoryIndex((prevIndex) => (prevIndex > 0 ? prevIndex - 1 : prevIndex));
	};

	const SelectedGardenBed = componentMap[bedConfig] || null;

	return (
		<Group justify="space-between" align="center" style={{
			backgroundColor: colors.darkerGrey,
			borderRadius: 20,
			height: '25vh'
		}}>
			{/* Left Side - Garden Bed Diagram */}
			<Stack align="center" justify="center" style={{
				//flex: '0 0 40%',
				//paddingRight: '15px'
			}}>
				{matchingGardenBed ? (
					<>
						{/* Garden Bed Container */}
						<Box style={{
							width: '40vw',
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
              backgroundColor: colors.white,
              borderRadius: '1.5vw',
              marginLeft: '2vw'
						}}>
							{SelectedGardenBed ? (
								<Suspense fallback={<div style={{ color: colors.white }}>Loading...</div>}>
									<SelectedGardenBed currentPlantCategory={currentPlantCategory} />
								</Suspense>
							) : (
								<Text style={{ color: colors.white, textAlign: 'center', fontSize: fontSize.sm }}>
									No garden bed layout found
								</Text>
							)}
						</Box>
						
						{/* Category Navigation Container */}
						<Box style={{
							width: '100%',
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
							marginTop: '0vw'
						}}>
							<Group justify="center" align="center" style={{ gap: '3px', paddingLeft: '2vw' }}>
								<Button
									variant="filled"
									onClick={goToPreviousCategory}
									disabled={currentCategoryIndex === 0}
									styles={{
										root: {
											backgroundColor: colors.buttonColor,
											color: colors.white,
											border: 'none',
											borderRadius: '1.5vw',
											padding: '2vw 2vw',
											fontWeight: 'bold',
											minHeight: '4vw',
											fontSize: fontSize.xs,
											opacity: currentCategoryIndex === 0 ? 0.5 : 1,
                      //marginLeft: '2vw'
										}
									}}
								>
									&lt; Previous
								</Button>
								
								<Text style={{
									color: colors.white,
									fontSize: fontSize.xl,
									fontWeight: 'bold',
									textAlign: 'center',
									margin: '0 2px'
								}}>
									{currentPlantCategory}
								</Text>
								
								<Button
									variant="filled"
									onClick={goToNextCategory}
									disabled={currentCategoryIndex === plantCategoriesList.length - 1}
									styles={{
										root: {
											backgroundColor: colors.buttonColor,
											color: colors.white,
											border: 'none',
											borderRadius: '6px',
											padding: '4px 8px',
											fontWeight: 'bold',
											minHeight: '30px',
											fontSize: fontSize.xs,
											opacity: currentCategoryIndex === plantCategoriesList.length - 1 ? 0.5 : 1
										}
									}}
								>
									Next &gt;
								</Button>
							</Group>
						</Box>
					</>
				) : (
					<Text style={{ color: colors.white, textAlign: 'center', fontSize: fontSize.sm }}>
						No matching garden bed layout found.
					</Text>
				)}
			</Stack>

			{/* Right Side - Plant Information */}
			<Box style={{
				flex: '1',
				display: 'flex',
				flexDirection: 'column',
				justifyContent: 'center',
			}}>
				{state.user.hasActiveSubscription && selectedPlantData && (
					<SelectedPlantInfoMobile selectedPlantData={selectedPlantData} />
				)}
				{!state.user.hasActiveSubscription && (
					<Box style={{
						textAlign: 'center',
            minHeight: '18vh'
					}}>
						<Text style={{
							color: colors.white,
							fontSize: fontSize.sm,
              fontWeight: 'bold'
						}}>
							Subscribe to see plant info
						</Text>
					</Box>
				)} 
			</Box>
		</Group>
	);
};

ChoosePlantSideWindowMobile.propTypes = {
	onCategoryChange: PropTypes.func.isRequired,
	selectedPlantData: PropTypes.object
};

export default ChoosePlantSideWindowMobile;
